import axios from "axios";

import { buildNcrmActivityCreatePayload } from "@/Utile/ncrmActivity.mjs";

export const COMPANY_EMAIL_SEND_ENDPOINT = "/company-email/send";

const trimText = (value) => String(value ?? "").trim();

const trimOrNull = (value) => {
  const trimmed = trimText(value);
  return trimmed || null;
};

export const createCompanyEmailDraft = ({
  to = "", subject = "", text = "", replyTo = "", purposeId = null,
} = {}) => ({
  to: trimText(to),
  subject: trimText(subject),
  text: trimText(text),
  replyTo: trimText(replyTo),
  purposeId: Number(purposeId) || null,
});

export const isCompanyEmailDraftValid = (draft = {}) => {
  return Boolean(
    trimText(draft.to) && trimText(draft.subject) && trimText(draft.text) && Number(draft.purposeId) > 0
  );
};

// `preferenceLocale`: language of the GDPR preference footer/link (S7.3a); the composer sends its UI language.
export const buildCompanyEmailSendPayload = (draft = {}, { preferenceLocale = null } = {}) => {
  const payload = {
    to: trimText(draft.to),
    subject: trimText(draft.subject),
    text: trimText(draft.text),
    purposeId: Number(draft.purposeId) || null,
  };

  const replyTo = trimOrNull(draft.replyTo);
  if (replyTo) payload.replyTo = replyTo;
  if (["ro", "en", "ru"].includes(preferenceLocale)) payload.preferenceLocale = preferenceLocale;

  return payload;
};

export const normalizeCompanyEmailSenderPreview = (preview = {}) => ({
  source: trimText(preview.source) || "company_default",
  sender: {
    fromEmail: trimText(preview.sender?.fromEmail),
    fromName: trimOrNull(preview.sender?.fromName),
  },
  transportAccountId: Number(preview.transportAccountId) || null,
  transportAccountType: trimText(preview.transportAccountType) || "company",
  providerKey: trimText(preview.providerKey) || "generic",
});

export const formatCompanyEmailSender = (sender = {}) => {
  const email = trimText(sender.fromEmail);
  const name = trimText(sender.fromName);
  if (!email) return "";
  return name ? `${name} <${email}>` : email;
};

export const buildCompanyEmailActivityContent = ({
  to = "",
  sender = {},
  messageId = "",
  source = "",
  text = "",
} = {}) => {
  const lines = [
    `To: ${trimText(to)}`,
    `From: ${formatCompanyEmailSender(sender)}`,
  ];
  const normalizedMessageId = trimText(messageId);
  const normalizedSource = trimText(source);
  if (normalizedMessageId) lines.push(`Message ID: ${normalizedMessageId}`);
  if (normalizedSource) lines.push(`Sender source: ${normalizedSource}`);
  lines.push("", trimText(text));
  return lines.join("\n").trim();
};

export const buildCompanyEmailActivityPayload = ({
  entityType,
  entityId,
  draft = {},
  sendResult = {},
  sentAt = new Date().toISOString(),
} = {}) => buildNcrmActivityCreatePayload({
  entityType,
  entityId,
  activityType: "email",
  status: "sent",
  priority: "normal",
  title: trimText(draft.subject),
  content: buildCompanyEmailActivityContent({
    to: draft.to,
    sender: sendResult.sender,
    messageId: sendResult.messageId,
    source: sendResult.source,
    text: draft.text,
  }),
  happenedAt: sentAt,
  dueAt: sentAt,
});

export const fetchCompanyEmailSenderPreview = () =>
  axios.get(`${COMPANY_EMAIL_SEND_ENDPOINT}/preview`);

export const sendCompanyEmail = (payload) =>
  axios.post(COMPANY_EMAIL_SEND_ENDPOINT, payload);
